import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { experimentService } from '../../services/experimentService';
import { ExperimentType, CreateExperimentRequest, SearchEngine } from '../../types/experiment.types';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const steps = [
  'Basic Info',
  'Type & Engines',
  'Pages',
  'Windows',
  'Review',
];

interface FormData extends Partial<CreateExperimentRequest> {
  controlPageIds?: number[];
}

const ExperimentForm: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    hypothesis: '',
    type: 'A_B_TEST' as ExperimentType,
    engines: [],
    treatmentPageIds: [],
    controlPageIds: [],
    windows: [],
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateExperimentRequest) => experimentService.createExperiment(data),
    onSuccess: (result) => {
      navigate(`/experiments/${result.id}`);
    },
  });

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async () => {
    const submitData: CreateExperimentRequest = {
      name: formData.name!,
      hypothesis: formData.hypothesis!,
      type: formData.type!,
      engines: formData.engines!,
      treatmentPageIds: formData.treatmentPageIds!,
      controlPageIds: formData.controlPageIds || [],
      windows: formData.windows!,
    };
    createMutation.mutate(submitData);
  };

  const handleEngineChange = (engine: SearchEngine) => {
    setFormData((prev) => ({
      ...prev,
      engines: prev.engines?.includes(engine)
        ? prev.engines.filter((e) => e !== engine)
        : [...(prev.engines || []), engine],
    }));
  };

  if (createMutation.isPending) return <LoadingSpinner />;
  if (createMutation.isError) return <ErrorMessage message="Failed to create experiment" />;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Create New Experiment
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Basic Information</Typography>
            <TextField
              label="Experiment Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              placeholder="e.g., Homepage Layout Redesign"
              required
            />
            <TextField
              label="Hypothesis"
              value={formData.hypothesis}
              onChange={(e) => setFormData({ ...formData, hypothesis: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe what you expect to happen and why"
              required
            />
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Type & Search Engines</Typography>
            
            <FormControl fullWidth>
              <InputLabel>Experiment Type</InputLabel>
              <Select
                value={formData.type}
                label="Experiment Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value as ExperimentType })}
              >
                <MenuItem value="A_B_TEST">A/B Test (Split Traffic)</MenuItem>
                <MenuItem value="BEFORE_AFTER">Before/After (DID)</MenuItem>
                <MenuItem value="TIME_SERIES">Time Series Analysis</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Search Engines</Typography>
            <FormGroup>
              {(['GOOGLE', 'BING'] as SearchEngine[]).map((engine) => (
                <FormControlLabel
                  key={engine}
                  control={
                    <Checkbox
                      checked={formData.engines?.includes(engine) || false}
                      onChange={() => handleEngineChange(engine)}
                    />
                  }
                  label={engine}
                />
              ))}
            </FormGroup>
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Select Pages</Typography>
            <Alert severity="info">
              Select treatment and control pages for this experiment
            </Alert>
            <TextField
              label="Treatment Page IDs (comma-separated)"
              value={formData.treatmentPageIds?.join(',') || ''}
              onChange={(e) => setFormData({
                ...formData,
                treatmentPageIds: e.target.value ? e.target.value.split(',').map(id => parseInt(id.trim())) : [],
              })}
              fullWidth
              placeholder="e.g., 1,2,3"
              required
            />
            <TextField
              label="Control Page IDs (comma-separated)"
              value={formData.controlPageIds?.join(',') || ''}
              onChange={(e) => setFormData({
                ...formData,
                controlPageIds: e.target.value ? e.target.value.split(',').map(id => parseInt(id.trim())) : [],
              })}
              fullWidth
              placeholder="Optional for before/after tests"
            />
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Measurement Windows</Typography>
            <Alert severity="info">
              Define pre and post measurement periods
            </Alert>
            <Typography variant="body2" color="textSecondary">
              Coming soon: Add multiple measurement windows with custom analysis methods
            </Typography>
          </Stack>
        )}

        {activeStep === 4 && (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Review</Typography>
            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Name</Typography>
                  <Typography variant="body2">{formData.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Type</Typography>
                  <Typography variant="body2">{formData.type}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Engines</Typography>
                  <Typography variant="body2">{formData.engines?.join(', ')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Treatment Pages</Typography>
                  <Typography variant="body2">{formData.treatmentPageIds?.join(', ')}</Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>

        <Stack direction="row" spacing={2}>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
          >
            Cancel
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              onClick={handleNext}
              variant="contained"
              endIcon={<ArrowForward />}
            >
              Next
            </Button>
          )}

          {activeStep === steps.length - 1 && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="success"
            >
              Create Experiment
            </Button>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default ExperimentForm;
